import classnames from 'classnames';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import { Loader } from 'views/components/Loader/Loader';
import { Icon, IconTypes } from 'views/components/Icon/Icon';
import { isUrlInternal } from 'tools/utilities/is-url-internal';

interface IProps {
  label: string;

  onClick?: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  url?: string;

  className?: string;
  icon?: IconTypes;
  iconClass?: string;
  id?: string;
  isIconOnly?: boolean;

  // Button only fields
  buttonType?: 'button' | 'submit' | 'reset';
  disabledClass?: string;
  isDisabled?: boolean;
  isFauxDisabled?: boolean;
  isLoading?: boolean;
  role?: string;
  title?: string;
}

/**
 * Button Component, configurable to produce a <button>,
 * <Link> or <a> tag.
 */
export class Button extends Component<IProps> {
  handleClick = (proxy: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const {
      onClick,
    } = this.props;

    if (onClick) {
      onClick(proxy);
    }
  };

  render() {
    const {
      buttonType = 'submit',
      className,
      disabledClass,
      icon,
      // iconClass,
      isIconOnly,
      id,
      isDisabled,
      isFauxDisabled,
      isLoading,
      label,
      role,
      title,
      url,
    } = this.props;

    const labelClassName = classnames({ 'u-hidden': isIconOnly });

    const innerContent = (
      <>
        {icon && <Icon name={icon} className={`icon icon-${icon}`} />}
        <span className={labelClassName}>{label}</span>
      </>
    );

    if (url) {
      // setup the common props for all variations
      const setupProps = {
        className,
        role,
        id,
        title,
      };

      // Remove any white space from the url that may have been added
      // accidentally in the cms.
      const cleanUrl = url.trim();

      const isInternalLink = isUrlInternal(cleanUrl);

      // If internal link then use the Link component so we don't
      // refresh the entire SPA
      if (isInternalLink) {
        return (
          <Link {...setupProps} to={cleanUrl} role={role}>
            {innerContent}
          </Link>
        );
      }

      // Otherwise ALWAYS open it in a new tab
      if (isInternalLink === false) {
        return (
          <a {...setupProps} href={cleanUrl} target="_blank" rel="noopener noreferrer">
            {innerContent}
          </a>
        );
      }
    }

    return (
      <button
        // eslint-disable-next-line react/button-has-type
        type={buttonType}
        className={classnames(className, {
          [disabledClass || 'Button is-disabled']: isDisabled,
          [disabledClass || 'Button is-disabled']: isFauxDisabled,
          'is-loading': isLoading,
          'has-icon': icon,
        })}
        onClick={this.handleClick}
        disabled={isDisabled}
        title={title}
        role={role}
        id={id}
      >
        {isLoading && (
          <div>
            <Loader />
            <span className={`${labelClassName} u-hidden`}>{label}</span>
          </div>
        )}

        {!isLoading && innerContent}
      </button>
    );
  }
}
